// Sequencer Connect - the wizard shell.
//
// This component owns ALL server state for one run (run + items) and hands it
// down to the six step components. The steps never fetch it back; they call the
// callbacks in SqStepProps and let the shell re-read. One reader means one
// truth: after any write, what the operator sees is what the server holds.
//
// The wizard is only ever rendered while the run is `draft` or `in_progress` -
// the two statuses the server itself accepts wizard writes in (wizardOpen in
// src/routes/sequencer-runs.ts). Every other status is the run in flight or
// finished, which is the progress view's job.
//
// SECURITY: the customer's sequencer API key and the per-mailbox passwords are
// write-only from the browser's point of view.
// None of them comes back on any response, so nothing in this tree can render
// one. StepApiKey clears its own input the moment the server confirms.

import { useCallback, useEffect, useRef, useState } from 'react';

import './SequencerConnect.css';
import { ProgressView } from './sequencer-connect/ProgressView';
import { StepAccounts } from './sequencer-connect/StepAccounts';
import { StepApiKey } from './sequencer-connect/StepApiKey';
import { StepParams } from './sequencer-connect/StepParams';
import { StepSequencer } from './sequencer-connect/StepSequencer';
import { StepSummary } from './sequencer-connect/StepSummary';
import { StepWorkspace } from './sequencer-connect/StepWorkspace';
import type { SqStepProps, SqToastKind } from './sequencer-connect/shared';
import {
  sqApi,
  sqErrorMessage,
  type SqRunDetail,
  type SqRunStatus,
} from './sequencerApi';

const TOAST_MS = 8000;

/** Poll cadence while the run is in flight. Same 3s as ForeignDnsRunDetail. */
const POLL_MS = 3000;

/**
 * How long a just-enqueued job is given to be picked up.
 *
 * Start and both retries answer the moment the jobs are ON the queue, not when a
 * worker takes one - and a mailbox sitting `pending` behind an unclaimed job
 * looks exactly like a run that is not moving. The window keeps the poll alive
 * across that gap; the first row that goes `running` closes it early.
 */
const PICKUP_WINDOW_MS = 60_000;

const STEPS: { n: number; label: string }[] = [
  { n: 1, label: 'Sequencer' },
  { n: 2, label: 'API key' },
  { n: 3, label: 'Accounts' },
  { n: 4, label: 'Workspace' },
  { n: 5, label: 'Parameters' },
  { n: 6, label: 'Summary' },
];

/** Same mapping as the run list. Repeated rather than exported from it because
 *  the two files paint different things and neither owns the other's badge. */
const STATUS_BADGE: Record<SqRunStatus, string> = {
  draft: 'muted',
  in_progress: 'info',
  running: 'running',
  qa: 'warn',
  completed: 'ok',
  failed: 'err',
};

const STATUS_LABEL: Record<SqRunStatus, string> = {
  draft: 'draft',
  in_progress: 'in progress',
  running: 'running',
  qa: 'QA check',
  completed: 'completed',
  failed: 'failed',
};

interface Toast {
  type: SqToastKind;
  message: string;
}

function fmtDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
}

export function SequencerConnectRunDetail({
  runId,
  onBack,
}: {
  runId: string;
  onBack: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<SqRunDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [stepBusy, setStepBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [pickupUntil, setPickupUntil] = useState<number | null>(null);

  /**
   * One toast at a time, one timer at a time.
   *
   * The previous timer has to be cancelled, not merely outlived: a toast raised
   * near the end of an earlier toast's window is otherwise wiped by that earlier
   * timer firing. The dropped-tag warning after Start exists ONLY as a toast, so
   * it is precisely the message a stray timer would truncate. Cleared on unmount
   * too, so the callback cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((type: SqToastKind, message: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  /**
   * Monotonic request id, so a slow getRun can never overwrite a newer one.
   *
   * The wizard reloads after every write, and two of those writes (Validate on
   * step 2, Parse on step 3) are slow calls to PlusVibe. A reload fired before
   * one of them landing afterwards would repaint the step with pre-write state
   * and invite the operator to do the work twice. Dropping every non-latest
   * response is what stops that.
   */
  const reloadSeq = useRef(0);
  const reload = useCallback(async (): Promise<void> => {
    const seq = (reloadSeq.current += 1);
    try {
      const next = await sqApi.getRun(runId);
      if (seq !== reloadSeq.current) return;
      setDetail(next);
      setLoadError(null);
    } catch (err) {
      if (seq !== reloadSeq.current) return;
      setLoadError(sqErrorMessage(err));
    }
  }, [runId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Is this run in flight?
   *
   * Read off `run.status` alone. `qa` counts: the QA pass resets and re-drives
   * items on its own, so the rows keep changing under a run that is no longer
   * `running`, and stopping the poll there would freeze the screen for the whole
   * verification pass.
   */
  const anyRunning =
    detail !== null && (detail.run.status === 'running' || detail.run.status === 'qa');

  // A row went live, so the pickup window has done its job and the normal rule
  // takes over from here.
  useEffect(() => {
    if (anyRunning && pickupUntil !== null) setPickupUntil(null);
  }, [anyRunning, pickupUntil]);

  useEffect(() => {
    if (pickupUntil === null) return undefined;
    const timer = window.setTimeout(
      () => setPickupUntil(null),
      Math.max(0, pickupUntil - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [pickupUntil]);

  // The only poll in this feature: on while the run is running or in QA (or
  // while a just-enqueued job is still waiting to be picked up), off otherwise.
  // A finished run never polls - its rows cannot change again.
  const polling = anyRunning || pickupUntil !== null;
  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => {
      void reload();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, reload]);

  /**
   * One last read after the run leaves flight.
   *
   * The poll that observed `completed` and the item rows it carried were read by
   * two separate queries, so a row that finished between them is still shown as
   * pending on the response that turns the poll off. This re-reads once, and
   * exactly once - `wasLive` is already false on the reload it triggers.
   */
  const wasLive = useRef(false);
  useEffect(() => {
    if (detail === null) return;
    if (wasLive.current && !anyRunning) void reload();
    wasLive.current = anyRunning;
  }, [detail, anyRunning, reload]);

  const notifyEnqueued = useCallback(() => {
    setPickupUntil(Date.now() + PICKUP_WINDOW_MS);
  }, []);

  /**
   * Move the pointer, server-side first.
   *
   * current_step lives on the run row precisely so a wizard resumes on another
   * machine, so nothing here keeps a local copy of it. The only failure that can
   * surface is RUN_CLOSED, when someone archived or started this run between the
   * shell's last read and this click; the toast is that race's whole handling,
   * and the reload right after re-reads the run into whatever it has become.
   */
  const goToStep = useCallback(
    async (step: number): Promise<void> => {
      setStepBusy(true);
      try {
        await sqApi.setStep(runId, step);
        await reload();
      } catch (err) {
        showToast('error', sqErrorMessage(err));
        await reload();
      } finally {
        setStepBusy(false);
      }
    },
    [runId, reload, showToast],
  );

  if (!detail) {
    return (
      <div className="sq">
        <div className="sq-scroll">
          <div className="sq-runheader">
            <button type="button" className="sq-btn sq-btn--ghost sq-btn--small" onClick={onBack}>
              Back
            </button>
            <h1 className="sq-title">Run</h1>
          </div>
          {loadError ? (
            <>
              <div className="sq-toast sq-toast--error">{loadError}</div>
              <div className="sq-card">
                <div className="sq-card-body">
                  <div className="sq-actions">
                    <button
                      type="button"
                      className="sq-btn sq-btn--primary"
                      onClick={() => {
                        setRetrying(true);
                        void reload().finally(() => setRetrying(false));
                      }}
                      disabled={retrying}
                    >
                      {retrying ? 'Retrying...' : 'Retry'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="sq-card">
              <div className="sq-empty">Loading run...</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { run } = detail;
  const inWizard = run.status === 'draft' || run.status === 'in_progress';
  // Warmuptool configures nothing after the connect, so it has no workspace or
  // parameters step: accounts go straight to the summary. Pointer values stay 1-6.
  const connectOnly = run.sequencer === 'warmuptool';

  // Step 4 is a workspace pick on PlusVibe and the workspace user id on Smartlead,
  // so the rail label is per-sequencer. Everything else is shared.
  const steps = connectOnly
    ? STEPS.filter((s) => s.n !== 4 && s.n !== 5)
    : run.sequencer === 'smartlead'
      ? STEPS.map((s) => (s.n === 4 ? { ...s, label: 'Workspace / User ID' } : s))
      : STEPS;

  /**
   * Why a step cannot be opened yet, or null when it can.
   *
   * Backwards is ALWAYS allowed before Start: a step already reached stays
   * reachable even when the data behind it has since been cleared, because
   * re-entering it is the recovery. Forwards, each step names the one piece of
   * run state it cannot work without.
   *
   * Step 3 is the interesting one on a resumed run. Archive purges the secrets,
   * and a purged run reads `has_api_key === false` however far the wizard had
   * got - so this sends the operator back to step 2 to re-validate a key rather
   * than to an accounts step whose Parse the server would refuse.
   */
  function lockReason(step: number): string | null {
    if (step <= run.current_step) return null;
    switch (step) {
      case 3:
        return run.has_api_key
          ? null
          : connectOnly
            ? 'Validate the API URL and key first.'
            : 'Validate the API key first - the workspace list comes from it.';
      case 4:
        return run.accounts_total > 0
          ? null
          : 'Paste the accounts sheet first - there are no mailboxes on this run yet.';
      case 5:
        // Smartlead gates step 5 on the workspace user id (it has no workspace to
        // pick); PlusVibe on the chosen workspace.
        if (run.sequencer === 'smartlead') {
          return run.sl_user_id !== null ? null : 'Set the workspace user id first.';
        }
        return run.workspace_id !== null ? null : 'Pick the workspace first.';
      case 6:
        if (connectOnly) {
          return run.accounts_total > 0
            ? null
            : 'Paste the accounts sheet first - there are no mailboxes on this run yet.';
        }
        return run.params !== null ? null : 'Save the sending parameters first.';
      default:
        // Steps 1 and 2 are always reachable: picking the sequencer again and
        // re-entering the key are the two recoveries for everything else.
        return null;
    }
  }

  /**
   * Is this step's OWN work finished?
   *
   * Deliberately not "its prerequisite is met" - sage means done in this design
   * system, and painting step 5 green because a workspace exists would be a
   * false claim about parameters nobody has saved.
   */
  function stepDone(step: number): boolean {
    switch (step) {
      case 1:
        // `sequencer` is non-null from creation (there is one sequencer), so the
        // pointer having left step 1 is the only honest evidence the operator
        // passed it.
        return run.current_step > 1;
      case 2:
        return run.has_api_key;
      case 3:
        return run.accounts_total > 0;
      case 4:
        // Smartlead's step 4 sets the workspace user id, not a workspace.
        return run.sequencer === 'smartlead' ? run.sl_user_id !== null : run.workspace_id !== null;
      case 5:
        return run.params !== null;
      case 6:
      default:
        // The summary's own work is starting the run, and that flips the status
        // out of the wizard entirely. Inside the wizard it is never done.
        return false;
    }
  }

  function railState(step: number): 'active' | 'done' | 'locked' | null {
    if (step === run.current_step) return 'active';
    if (stepDone(step)) return 'done';
    if (lockReason(step) !== null) return 'locked';
    return null;
  }

  const stepProps: SqStepProps = {
    runId,
    detail,
    reload,
    notifyEnqueued,
    goToStep,
    toast: showToast,
  };

  function renderStep(): JSX.Element {
    if (connectOnly && run.current_step >= 4) return <StepSummary {...stepProps} />;
    switch (run.current_step) {
      case 1:
        return <StepSequencer {...stepProps} />;
      case 2:
        return <StepApiKey {...stepProps} />;
      case 3:
        return <StepAccounts {...stepProps} />;
      case 4:
        return <StepWorkspace {...stepProps} />;
      case 5:
        // The params form is the one step whose FIELDS differ per platform, not
        // just its copy - StepParams dispatches to the right form component by
        // run.sequencer, all of them sharing TagsPanel.
        return <StepParams {...stepProps} />;
      case 6:
      default:
        // POST /:id/step is the only writer of current_step and its zod schema
        // caps it at 1..6, so the default arm is unreachable through the API.
        // The column has no check constraint, though, so a hand-run UPDATE could
        // land here; the summary is the safe place for that, because it reads
        // the run and writes nothing until it is asked to.
        return <StepSummary {...stepProps} />;
    }
  }

  // A run whose credentials were purged and which is somehow still in the
  // wizard. NOT simply `!has_api_key`: a fresh draft has never held a key, and
  // telling the operator theirs "was destroyed" would send them looking for
  // something that never happened.
  const purged = inWizard && run.secrets_purged_at !== null && !run.has_api_key;

  return (
    <div className="sq">
      <div className="sq-scroll">
        <div className="sq-runheader">
          <button type="button" className="sq-btn sq-btn--ghost sq-btn--small" onClick={onBack}>
            Back
          </button>
          <h1 className="sq-title">{run.name}</h1>
          <span className={`sq-badge sq-badge--${STATUS_BADGE[run.status]}`}>
            {STATUS_LABEL[run.status]}
          </span>
          {run.archived_at && <span className="sq-badge sq-badge--muted">archived</span>}
        </div>

        <div className="sq-runmeta">
          {inWizard && (
            <span>
              <strong>Step</strong>
              {connectOnly ? Math.min(run.current_step, steps.length) : run.current_step} of{' '}
              {steps.length}
            </span>
          )}
          <span>
            <strong>Accounts</strong>
            {run.accounts_total}
          </span>
          {run.sequencer === 'smartlead' ? (
            <span>
              <strong>User ID</strong>
              {run.sl_user_id ?? 'not set'}
            </span>
          ) : connectOnly ? (
            <span>
              <strong>Instance</strong>
              {run.base_url ?? 'not set'}
            </span>
          ) : (
            <span>
              <strong>Workspace</strong>
              {run.workspace_name ?? 'not picked'}
            </span>
          )}
          {!connectOnly && (
            <span>
              <strong>Tag</strong>
              {run.tag_name}
            </span>
          )}
          <span>
            <strong>
              {run.sequencer === 'instantly'
                ? 'Instantly key'
                : run.sequencer === 'bison'
                  ? 'Bison key'
                  : run.sequencer === 'smartlead'
                    ? 'Smartlead key'
                    : connectOnly
                      ? 'Warmuptool key'
                      : 'PlusVibe key'}
            </strong>
            {run.has_api_key ? `held, verified ${fmtDate(run.api_key_verified_at)}` : 'not held'}
          </span>
          <span>
            <strong>Created</strong>
            {fmtDate(run.created_at)} by {run.created_by}
          </span>
        </div>

        {loadError && <div className="sq-toast sq-toast--error">{loadError}</div>}
        {toast && <div className={`sq-toast sq-toast--${toast.type}`}>{toast.message}</div>}

        {/* Archive is orthogonal to status, so a run can be archived while its
            status still reads draft. The wizard still renders - the record is
            worth reading - but wizardOpen refuses every write on it, so say so
            once here rather than letting each step discover it with a 409. */}
        {inWizard && run.archived_at && (
          <div className="sq-toast sq-toast--warn">
            This run is archived and its credentials were purged. It can no longer be edited or
            started - create a new run to connect more mailboxes.
          </div>
        )}

        {purged && !run.archived_at && (
          <div className="sq-toast sq-toast--warn">
            This run's borrowed credentials were purged {fmtDate(run.secrets_purged_at)}. Re-enter
            the API key on step 2, and paste the accounts sheet again on step 3 - the mailbox
            passwords went with it.
          </div>
        )}

        {inWizard ? (
          <>
            <div className="sq-rail">
              {steps.map((s, index) => {
                const state = railState(s.n);
                const reason = lockReason(s.n);
                return (
                  <button
                    key={s.n}
                    type="button"
                    className={`sq-rail-step${state ? ` sq-rail-step--${state}` : ''}`}
                    disabled={reason !== null || stepBusy}
                    title={reason ?? s.label}
                    onClick={() => {
                      if (s.n !== run.current_step) void goToStep(s.n);
                    }}
                  >
                    <span className="sq-rail-num">{index + 1}</span>
                    <span className="sq-rail-label">{s.label}</span>
                  </button>
                );
              })}
            </div>

            {renderStep()}
          </>
        ) : (
          <ProgressView
            runId={runId}
            detail={detail}
            reload={reload}
            notifyEnqueued={notifyEnqueued}
            toast={showToast}
          />
        )}
      </div>
    </div>
  );
}
